import React, {useState} from "react";
import {Box, ButtonBase, Dialog, DialogContent, IconButton, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import {observer} from "mobx-react-lite";
import {Story} from "../utils/story";
import {CastMember} from "../utils/characters";
import CharacterAvatar from "./CharacterAvatar";

const useStyles = makeStyles((theme) => ({
    row: {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
        margin: theme.spacing(-1, 0, 3),
    },
    member: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 64,
        borderRadius: 12,
        padding: theme.spacing(0.5, 0),
    },
    name: {
        marginTop: theme.spacing(0.5),
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    paper: {
        borderRadius: 20,
        width: "min(440px, calc(100vw - 32px))",
    },
    close: {
        position: "absolute",
        top: theme.spacing(1),
        right: theme.spacing(1),
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    title: {
        fontFamily: theme.typography.h1.fontFamily,
        fontWeight: 600,
        fontSize: "1.4rem",
        lineHeight: 1.2,
    },
    label: {
        display: "block",
        color: theme.palette.primary.main,
        fontSize: "0.68rem",
        lineHeight: 1.6,
        marginTop: theme.spacing(1.5),
    },
    text: {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
}));

// The story's characters as small portraits above the text; tapping one shows who they are
const StoryCast = observer(({story}: { story: Story }) => {
    const classes = useStyles();
    const [shown, setShown] = useState<CastMember | null>(null);

    if (story.characters.length === 0) {
        return null;
    }
    return (
        <>
            <Box className={classes.row}>
                {story.characters.map((member) => (
                    <ButtonBase key={member.characterId} className={classes.member} onClick={() => setShown(member)}
                                aria-label={`About ${member.name}`}>
                        <CharacterAvatar name={member.name} portrait={member.portrait} size={44}/>
                        <span className={classes.name}>{member.name}</span>
                    </ButtonBase>
                ))}
            </Box>
            <Dialog open={shown !== null} onClose={() => setShown(null)} classes={{paper: classes.paper}}>
                {shown && (
                    <DialogContent style={{padding: 24, position: "relative"}}>
                        <IconButton aria-label="Close" className={classes.close} onClick={() => setShown(null)}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                        <Box className={classes.header}>
                            <CharacterAvatar name={shown.name} portrait={shown.portrait} size={96}/>
                            <Box>
                                <Typography className={classes.title}>{shown.name}</Typography>
                                <Typography color="textSecondary">{shown.age}</Typography>
                            </Box>
                        </Box>
                        {shown.relationship && <CastDetail label="Role and relationships" text={shown.relationship}/>}
                        {shown.looks && <CastDetail label="Looks" text={shown.looks}/>}
                        {shown.personality && <CastDetail label="Personality" text={shown.personality}/>}
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
});

function CastDetail({label, text}: { label: string, text: string }) {
    const classes = useStyles();
    return (
        <>
            <Typography variant="overline" className={classes.label}>{label}</Typography>
            <Typography variant="body2" className={classes.text}>{text}</Typography>
        </>
    );
}

export default StoryCast;
